import {
  DataTableVirtualized,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  type DataTableVirtualizedProps,
  useDataTable
} from 'design-system';
import { useCallback, useMemo, useState } from 'react';

import { TABLE_ROW_HEIGHT_PX } from '@/config/constants';
import { DateTextVariants, useDateText } from '@/features/_shared/user';

import type { SpotHistoryTableColumnType, SpotHistoryTableRowType } from '../../types';
import { TABLE_HEADER_HEIGHT_PX } from './constants';
import { getSpotHistoryColumnId } from './spotHistoryColumn';
import {
  getSpotHistoryTableColumns,
  renderSpotHistoryHeaderDetails,
  renderSpotHistoryTableHeader
} from './spotHistoryTableColumns';
import type { SpotHistoryDataRow } from './types';
import { useColumnWidths } from './useColumnWidths';

type SpotHistoryTableProps = {
  columns: SpotHistoryTableColumnType[];
  rows: SpotHistoryTableRowType[];
  layout?: { fullWidth?: boolean; height?: number | string } | Record<string, string>;
};

const DEFAULT_VISIBLE_ROW_COUNT = 20;
const EMPTY_CONTENT_HEIGHT_PX = 96;

function resolveMaxHeight(layoutHeight: unknown, rowCount: number) {
  const configuredRows = Number(layoutHeight);
  const visibleRows =
    Number.isFinite(configuredRows) && configuredRows > 0
      ? configuredRows
      : Math.min(rowCount, DEFAULT_VISIBLE_ROW_COUNT);
  const bodyHeight = rowCount === 0 ? EMPTY_CONTENT_HEIGHT_PX : visibleRows * TABLE_ROW_HEIGHT_PX;
  return bodyHeight + TABLE_HEADER_HEIGHT_PX;
}

const getRowId = (row: SpotHistoryDataRow) => row.id;

const SpotHistoryTable = ({ columns, rows, layout }: SpotHistoryTableProps) => {
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const { formatDateString } = useDateText();
  const { columnSizing, onColumnSizingChange, persistColumnWidth } = useColumnWidths();
  const toggleDetails = useCallback(() => {
    setDetailsExpanded(expanded => !expanded);
  }, []);

  const displayRows = useMemo<SpotHistoryDataRow[]>(
    () =>
      rows.map((row, rowIndex) => ({
        id: `${row.date}-${rowIndex}`,
        date: row.date,
        formattedDate: formatDateString(row.date, DateTextVariants.FULL),
        values: Object.fromEntries(
          columns.map((column, columnIndex) => [
            getSpotHistoryColumnId(column, columnIndex),
            row.values[columnIndex] ?? '-'
          ])
        )
      })),
    [columns, formatDateString, rows]
  );
  const tableColumns = useMemo(() => getSpotHistoryTableColumns(columns), [columns]);
  const table = useDataTable({
    columnSizing,
    columns: tableColumns,
    data: displayRows,
    getRowId,
    onColumnSizingChange
  });
  const maxHeight = resolveMaxHeight(layout?.height, displayRows.length);
  const renderHeader = useCallback<
    NonNullable<DataTableVirtualizedProps<SpotHistoryDataRow>['renderHeader']>
  >(
    header =>
      renderSpotHistoryTableHeader(header, {
        expanded: detailsExpanded,
        onResizeEnd: persistColumnWidth,
        onToggleDetails: toggleDetails
      }),
    [detailsExpanded, persistColumnWidth, toggleDetails]
  );
  const renderHeaderFooter = useCallback(() => renderSpotHistoryHeaderDetails(columns), [columns]);

  if (columns.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>No spot history columns configured.</EmptyTitle>
          <EmptyDescription>Add a market column to view spot history.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <section
      aria-label='Spot history table viewport'
      className='min-w-0'
      data-max-height={maxHeight}
      style={{ height: maxHeight, maxHeight }}
    >
      <DataTableVirtualized
        aria-label='Spot history'
        columnOverscan={2}
        emptyContent='No spot history available.'
        estimateSize={TABLE_ROW_HEIGHT_PX}
        renderHeader={renderHeader}
        renderHeaderFooter={detailsExpanded ? renderHeaderFooter : undefined}
        table={table}
        virtualizeColumns
      />
    </section>
  );
};

export default SpotHistoryTable;
